<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import Button from './ui/button.svelte';
  import Switch from './ui/switch.svelte';
  import { Dialog } from 'bits-ui';
  import { cn } from '$lib/utils';
  import { t } from '$lib/i18n';
  import { browser } from '$app/environment';
  import Link from '$components/ui/link.svelte';
  import { buildPath } from '$lib/utils/paths';
  import { cva } from 'class-variance-authority';
  import { analyticsEvent, AnalyticsEvents } from '$lib/analytics';
  import { fade, fly, scale } from 'svelte/transition';
  import { aboveLg } from '$lib/stores/breakpoints';
  import { cubicOut } from 'svelte/easing';

  const base = cva(
    `
    fixed 
    left-1/2 
    sm:bottom-1/2 
    bottom-0
    z-50

    flex
    flex-col

    w-full 
    sm:max-w-[677px]
    max-w-full
    md:w-full 
    max-h-[90%] 
    bg-highlight
  
    shadow-popover 
    outline-none

    sm:rounded-md 
    rounded-tl-md 
    rounded-tr-md

    -translate-x-1/2 
    sm:translate-y-1/2 
    translate-y-0 
  `
  );

  const cookieBanner = cva(
    [
      // Base styles
      'fixed z-50 flex flex-col gap-2',
      'border-[0.5px] border-primary/14',
      'text-primary',

      // Spacing
      'p-3 sm:p-4',

      // Position
      'left-1/2 -translate-x-1/2 ',
      'bottom-2',
      'sm:left-2 sm:-translate-x-0',

      // Size
      'w-[97%] sm:max-w-[220px]',

      // Corners
      'rounded-md',

      // Colors
      'bg-highlight'
    ].join(' ')
  );

  const dispatch = createEventDispatcher<{ close: undefined }>();

  let showManageDialog = false;

  let show = browser && !localStorage?.getItem('cookies_consent');
  let title: HTMLHeadingElement;
  let buttons: HTMLDivElement;
  let modalOffset = '0px';
  let consent = {
    necessary: true,
    analytics: true,
    marketing: true
  };

  const close = () => {
    show = false;
    showManageDialog = false;
    dispatch('close');
  };

  function setConsent() {
    // Accept all: all consent values to true by default
    const cookies_consent: { [k: string]: 'granted' | 'denied' } = {
      functionality_storage: consent.necessary ? 'granted' : 'denied',
      security_storage: consent.necessary ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      personalization_storage: consent.marketing ? 'granted' : 'denied'
    };

    analyticsEvent({
      event: AnalyticsEvents.COOKIES_CONSENT,
      consent_functional: consent.necessary,
      consent_analytical: consent.analytics,
      consent_marketing: consent.marketing
    });

    // Saving consent to local storage, for not appear again
    localStorage.setItem('cookies_consent', JSON.stringify(cookies_consent));

    // TODO: removed while Harper is testing acceptance of cookies events
    /* if (browser) {
      window.gtag('consent', 'update', cookies_consent);
      localStorage.setItem('cookies_consent', JSON.stringify(cookies_consent));
    } */

    // close modal
    close();
  }

  onMount(() => {
    modalOffset = (title?.clientHeight || 0) + (buttons?.clientHeight || 0) + 'px';
  });
</script>

{#if show}
  <div class={cn(cookieBanner())}>
    <p class="text-primary text-sm font-medium leading-relaxed mb-1">
      {$t('cookies.modal.general.description')}
      <Link
        class="underline lowercase after:content-['.']"
        target="_blank"
        href={$buildPath({
          path: $t('auth.legal.privacy.link')
        })}
        style="box-shadow: none !important"
      >
        {$t('auth.legal.privacy')}
      </Link>
    </p>

    <div bind:this={buttons} class="flex sm:rounded-b-md justify-end sm:justify-end gap-1">
      <Button
        variant="ghost"
        class="font-small px-3 h-8"
        size="sm"
        on:click={() => (showManageDialog = true)}
      >
        {$t('cookies.modal.general.manage-cookies')}
      </Button>
      <Button variant="primary" class="font-small px-3 h-8" size="sm" on:click={setConsent}>
        {$t('cookies.modal.general.accept-all')}
      </Button>
    </div>
  </div>
{/if}

<Dialog.Root
  open={showManageDialog}
  closeOnEscape={false}
  closeOnOutsideClick={false}
  preventScroll={false}
>
  <Dialog.Trigger />
  <Dialog.Portal>
    <Dialog.Overlay
      transition={fade}
      transitionConfig={{ duration: 250 }}
      class="fixed inset-0 z-50 bg-primary/40"
    />
    <Dialog.Content
      transition={$aboveLg ? scale : fly}
      transitionConfig={$aboveLg
        ? { start: 0.5, duration: 300 }
        : { y: '100%', duration: 300, easing: cubicOut }}
      class={cn(base())}
    >
      <Dialog.Title
        bind:el={title}
        class="mb-1 px-4 pt-4 h-auto leading-[1.25] sm:leading-[1.25] text-lg sm:text-lg font-semibold sm:font-semibold"
        >{$t('cookies.modal.title')}</Dialog.Title
      >
      <div class={cn('px-4 overflow-y-auto pb-4', `max-h-[calc(90svh-${modalOffset})]`)}>
        <p class="text-primary text-sm sm:text-lg font-medium leading-relaxed mb-4">
          {$t('cookies.modal.manage.description')}
        </p>

        <hr class="my-4 block border-t-0 h-px bg-primary/4" />

        <div class="mb-5">
          <h6 class="mb-2">
            <Switch
              name="consent-necessary"
              id="consent-necessary"
              label={$t('cookies.modal.manage.functional.title')}
              disabled
              bind:checked={consent.necessary}
              value={(consent.necessary || false).toString()}
            />
          </h6>
          <p
            class="text-primary/80 text-sm sm:text-lg font-small sm:font-medium leading-relaxed mb-4"
          >
            {$t('cookies.modal.manage.functional.description')}
          </p>
        </div>

        <div class="mb-5">
          <h6 class="mb-2">
            <Switch
              name="consent-analytics"
              id="consent-analytics"
              label={$t('cookies.modal.manage.analytics.title')}
              bind:checked={consent.analytics}
              value={(consent.analytics || false).toString()}
            />
          </h6>
          <p class="text-primary/80 text-sm sm:text-lg font-medium leading-relaxed mb-4">
            {$t('cookies.modal.manage.analytics.description')}
          </p>
        </div>

        <div>
          <h6 class="mb-2">
            <Switch
              name="consent-marketing"
              id="consent-marketing"
              label={$t('cookies.modal.manage.marketing.title')}
              bind:checked={consent.marketing}
              value={(consent.marketing || false).toString()}
            />
          </h6>
          <p class="text-primary/80 text-sm sm:text-lg font-medium leading-relaxed mb-4">
            {$t('cookies.modal.manage.marketing.description')}
          </p>
        </div>
      </div>

      <div
        bind:this={buttons}
        class="flex p-4 border-t border-primary/4 sm:rounded-b-md bg-highlight justify-end gap-3"
      >
        <Button
          variant="ghost"
          size="sm"
          class="font-small px-3 h-8"
          on:click={() => (showManageDialog = false)}>{$t('cookies.modal.manage.back')}</Button
        >
        <Button variant="primary" size="sm" class="font-small px-3 h-8" on:click={setConsent}
          >{$t('cookies.modal.manage.save')}</Button
        >
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
